import type { IconType } from "react-icons";
import {
  FaAward,
  FaFileSignature,
  FaFileAlt,
  FaInfoCircle,
  FaPaperPlane,
  FaShieldAlt,
  FaTasks,
  FaUserCheck,
  FaUserTie,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import type { PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";

export const BID_WIZARD_STEPS = [
  { key: "intro", icon: FaInfoCircle, live: true },
  { key: "supplier", icon: FaUserTie, live: true },
  { key: "exclusion", icon: FaUserCheck, live: true },
  { key: "qualification", icon: FaTasks, live: true },
  { key: "awardCriteria", icon: FaAward, live: true },
  { key: "minimumRequirements", icon: FaShieldAlt, live: true },
  { key: "documents", icon: FaFileAlt, live: true },
  { key: "cover", icon: FaFileSignature, live: true },
  { key: "review", icon: FaPaperPlane, live: true },
] as const satisfies ReadonlyArray<{ key: string; icon: IconType; live: boolean }>;

export type BidStep = (typeof BID_WIZARD_STEPS)[number]["key"];

export const BID_WIZARD_STEP_KEYS = BID_WIZARD_STEPS.map((s) => s.key) as readonly BidStep[];

// Whether each conditionally-shown step has content to answer. Derived from the event with
// bidStepVisibility(); threaded through the wizard so nav, neighbors and the URL guard agree.
export interface BidStepVisibility {
  collectSupplierPrice: boolean;
  // Server-derived: false when the supplier declared it where they were admitted — with the
  // participation request, or at admission to the system — so the tender wizard drops those steps.
  espdTravelsWithBid: boolean;
  hasExclusionCriteria: boolean;
  hasSelectionCriteria: boolean;
  hasMinimumRequirements: boolean;
  hasAwardCriteria: boolean;
}

export const bidStepVisibility = (event: PortalSourcingEventDetailDto): BidStepVisibility => ({
  collectSupplierPrice: event.collectSupplierPrice ?? true,
  espdTravelsWithBid: event.collectsEspdWithBid ?? true,
  hasExclusionCriteria: (event.applicableExclusionCriteria ?? []).length > 0,
  hasSelectionCriteria: (event.selectionCriteria ?? []).length > 0,
  hasMinimumRequirements: (event.minimumRequirements ?? []).length > 0,
  hasAwardCriteria: (event.awardCriteria?.criteria ?? []).length > 0,
});

const isStepVisible = (key: BidStep, v: BidStepVisibility): boolean => {
  switch (key) {
    // The tendered price is answered inside this step, so it stays whenever either is collected.
    case "awardCriteria":
      return v.collectSupplierPrice || v.hasAwardCriteria;
    case "supplier":
      return v.espdTravelsWithBid;
    case "exclusion":
      return v.espdTravelsWithBid && v.hasExclusionCriteria;
    case "qualification":
      return v.espdTravelsWithBid && v.hasSelectionCriteria;
    case "minimumRequirements":
      return v.hasMinimumRequirements;
    default:
      return true;
  }
};

export const visibleBidSteps = (v: BidStepVisibility): (typeof BID_WIZARD_STEPS)[number][] =>
  BID_WIZARD_STEPS.filter((s) => isStepVisible(s.key, v));

export const visibleBidStepKeys = (v: BidStepVisibility): BidStep[] => visibleBidSteps(v).map((s) => s.key);

export const isLiveStep = (key: BidStep): boolean => BID_WIZARD_STEPS.find((s) => s.key === key)?.live ?? false;

export const useBidStepLabel = (): ((key: BidStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "intro":
        return t("About the procurement");
      case "supplier":
        return t("About the supplier");
      case "awardCriteria":
        return t("Award criteria");
      case "exclusion":
        return t("Grounds for exclusion");
      case "qualification":
        return t("Qualification requirements");
      case "minimumRequirements":
        return t("Minimum requirements");
      case "documents":
        return t("Offer documents");
      case "cover":
        return t("Cover letter");
      case "review":
        return t("Summary and submission");
    }
  };
};

export const bidStepNeighbors = (
  current: BidStep,
  v: BidStepVisibility,
): { previous: BidStep | undefined; next: BidStep | undefined } => {
  const keys = visibleBidStepKeys(v);
  const idx = keys.indexOf(current);
  return {
    previous: idx > 0 ? keys[idx - 1] : undefined,
    next: idx >= 0 && idx < keys.length - 1 ? keys[idx + 1] : undefined,
  };
};
